"use client";

import { useTransition } from "react";
import { Languages, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { setLocale } from "@/app/actions/locale";
import { useI18n } from "@/lib/i18n/provider";
import { LOCALES, type Locale } from "@/lib/i18n/types";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LABEL: Record<Locale, string> = { pt: "Português (BR)", en: "English (US)" };
const SHORT: Record<Locale, string> = { pt: "PT", en: "EN" };

/**
 * Seletor de idioma da interface — feature real, disponível para qualquer usuário.
 *
 * De propósito NÃO é um modo escondido para revisor: entregar uma experiência diferente
 * para quem avalia o app é violação de política da Meta. O que existe é inglês de verdade
 * no produto; o screencast só usa o que qualquer lojista pode usar.
 *
 * `variant`:
 *  - `header` — botão fantasma do header do dashboard (padrão)
 *  - `outline` — botão com borda, para telas DESLOGADAS (landing e /login), onde o header
 *    do dashboard não existe. Sem ele o idioma seria inalcançável justamente nas telas em
 *    que o screencast começa: a Meta exige gravar "from logout to login".
 */
export function LanguageSwitcher({ variant = "header" }: { variant?: "header" | "outline" }) {
    const { locale, dict } = useI18n();
    const [pending, startTransition] = useTransition();

    const choose = (value: Locale) => {
        if (value === locale) return;
        startTransition(() => {
            void setLocale(value);
        });
    };

    return (
        <div className="relative group/lang">
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        aria-label={dict.common.language}
                        disabled={pending}
                        className={cn(
                            "flex items-center justify-center transition-colors duration-150",
                            variant === "outline"
                                ? "h-9 w-9 rounded-[8px] border border-border bg-background text-foreground hover:bg-accent/[.08]"
                                : "min-h-[44px] sm:min-h-0 w-[34px] h-[34px] rounded-[8px] text-muted-foreground hover:bg-accent/[.08] hover:text-foreground",
                            pending && "opacity-60"
                        )}
                    >
                        <Languages
                            className={variant === "outline" ? "h-[1.05rem] w-[1.05rem]" : "h-[15px] w-[15px]"}
                            strokeWidth={2}
                        />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-popover border-border rounded-xl shadow-md mt-2">
                    <DropdownMenuLabel className="flex items-center gap-2 text-foreground text-xs">
                        <Languages className="w-3.5 h-3.5 text-accent" />
                        {dict.common.language}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-border" />
                    {LOCALES.map((value) => (
                        <DropdownMenuItem
                            key={value}
                            onClick={() => choose(value)}
                            className="cursor-pointer rounded-md text-xs flex items-center gap-2.5"
                        >
                            <span className="w-6 text-[10px] font-bold tracking-wide text-muted-foreground">
                                {SHORT[value]}
                            </span>
                            <span className="flex-1">{LABEL[value]}</span>
                            {locale === value && <Check className="w-3.5 h-3.5 text-accent" />}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
            <span
                className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-1.5 px-2 py-[0.1875rem] rounded-[5px] text-[0.625rem] font-semibold whitespace-nowrap opacity-0 group-hover/lang:opacity-100 transition-opacity duration-150 z-50"
                style={{ backgroundColor: "#0f0c20", color: "#edeaf5" }}
            >
                {dict.common.language}
            </span>
        </div>
    );
}
